'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { Edit2, Trash2, MapPin, AlertTriangle, Wifi, WifiOff, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

// Backend integration: GET /api/assets?type=vehicle
const vehicles = [
  { id: 'veh-001', unit: 'Unit-14', year: 2022, make: 'Ford', model: 'F-350 Super Duty', vin: '1FT8W3DT3NEC44201', license: 'TX-PBR-4421', status: 'en-route', gpsStatus: 'strong', driver: 'Travis Reyes', jobId: 'FJ-2841', lastLocation: 'Hwy 90, mm 42', odometer: 84320, fuelLevel: 62, nextService: '87,000 mi', alerts: 1 },
  { id: 'veh-002', unit: 'Unit-08', year: 2021, make: 'Chevy', model: 'Silverado 2500 HD', vin: '1GC4YPE75MF220847', license: 'TX-DST-8834', status: 'on-site', gpsStatus: 'strong', driver: 'Maria Gutierrez', jobId: 'FJ-2838', lastLocation: 'Block 14, Andrews TX', odometer: 61045, fuelLevel: 78, nextService: '65,000 mi', alerts: 0 },
  { id: 'veh-003', unit: 'Unit-22', year: 2020, make: 'Dodge', model: 'Ram 2500', vin: '3C6UR5DL9LG298177', license: 'TX-MID-1122', status: 'offline', gpsStatus: 'lost', driver: 'Kyle Odom', jobId: undefined, lastLocation: 'FM 1788, Midland (last known)', odometer: 112840, fuelLevel: 45, nextService: 'OVERDUE', alerts: 2 },
  { id: 'veh-004', unit: 'Unit-05', year: 2023, make: 'Ford', model: 'F-250 XLT', vin: '1FT7W2BTXPEC11044', license: 'TX-WTX-7721', status: 'returning', gpsStatus: 'strong', driver: 'Darnell Brooks', jobId: 'FJ-2833', lastLocation: 'US-285 S, Reeves County', odometer: 93200, fuelLevel: 88, nextService: '95,000 mi', alerts: 0 },
  { id: 'veh-005', unit: 'Unit-19', year: 2022, make: 'Toyota', model: 'Tundra TRD Pro', vin: '5TFDY5F13NX012984', license: 'TX-MID-3309', status: 'en-route', gpsStatus: 'strong', driver: 'Sofia Castillo', jobId: 'FJ-2829', lastLocation: 'US-385 N, Eddy County', odometer: 43670, fuelLevel: 55, nextService: '50,000 mi', alerts: 0 },
  { id: 'veh-006', unit: 'Unit-03', year: 2021, make: 'Ram', model: '3500 Laramie', vin: '3C63RRJL7MG511802', license: 'TX-PBR-0033', status: 'available', gpsStatus: 'strong', driver: undefined, jobId: undefined, lastLocation: 'Yard, Midland TX', odometer: 57120, fuelLevel: 95, nextService: '60,000 mi', alerts: 0 },
  { id: 'veh-007', unit: 'Unit-11', year: 2019, make: 'Ford', model: 'F-150 XLT', vin: '1FTEW1E47KFA90211', license: 'TX-ODS-5577', status: 'maintenance', gpsStatus: 'weak', driver: undefined, jobId: undefined, lastLocation: 'Odessa Service Center', odometer: 138440, fuelLevel: 30, nextService: 'In Service', alerts: 1 },
  { id: 'veh-008', unit: 'Unit-17', year: 2023, make: 'Chevy', model: 'Tahoe Z71', vin: '1GNSKCKD9PR441177', license: 'TX-MID-8801', status: 'available', gpsStatus: 'strong', driver: undefined, jobId: undefined, lastLocation: 'Yard, Midland TX', odometer: 22340, fuelLevel: 91, nextService: '25,000 mi', alerts: 0 },
];

type SortField = 'unit' | 'make' | 'odometer' | 'fuelLevel';
type SortDir = 'asc' | 'desc';

export default function VehicleTable() {
  const [sortField, setSortField] = useState<SortField>('unit');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [search, setSearch] = useState('');

  const filtered = vehicles.filter((v) => {
    const q = search.toLowerCase();
    return !q || v.unit.toLowerCase().includes(q) || v.make.toLowerCase().includes(q) || v.model.toLowerCase().includes(q) || (v.driver ?? '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'unit') return a.unit.localeCompare(b.unit) * dir;
    if (sortField === 'make') return a.make.localeCompare(b.make) * dir;
    if (sortField === 'odometer') return (a.odometer - b.odometer) * dir;
    if (sortField === 'fuelLevel') return (a.fuelLevel - b.fuelLevel) * dir;
    return 0;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: SortField }) => (
    <span className="inline-flex flex-col ml-1">
      <ChevronUp size={9} className={sortField === field && sortDir === 'asc' ? 'text-amber-400' : 'text-zinc-700'} />
      <ChevronDown size={9} className={sortField === field && sortDir === 'desc' ? 'text-amber-400' : 'text-zinc-700'} />
    </span>
  );

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
        <input
          type="text"
          placeholder="Search vehicles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 rounded-md px-3 py-1.5 text-xs text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 transition-all w-56"
        />
        <span className="text-xs text-zinc-600 ml-auto">{sorted.length} vehicles</span>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300" onClick={() => handleSort('unit')}>Unit <SortIcon field="unit" /></th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300" onClick={() => handleSort('make')}>Make / Model <SortIcon field="make" /></th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">License</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Status</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">GPS</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Driver</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Last Location</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300" onClick={() => handleSort('odometer')}>Odometer <SortIcon field="odometer" /></th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300" onClick={() => handleSort('fuelLevel')}>Fuel <SortIcon field="fuelLevel" /></th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Next Service</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {sorted.map((v) => (
              <tr key={v.id} className="group hover:bg-zinc-800/40 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-amber-400">{v.unit}</span>
                    {v.alerts > 0 && <AlertTriangle size={11} className="text-red-400" />}
                  </div>
                  <p className="text-[10px] text-zinc-600 font-mono">{v.year}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs font-medium text-zinc-200">{v.make} {v.model}</p>
                  <p className="text-[10px] text-zinc-600 font-mono truncate max-w-[130px]">{v.vin}</p>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-400">{v.license}</td>
                <td className="px-4 py-3"><StatusBadge status={v.status as any} /></td>
                <td className="px-4 py-3">
                  {v.gpsStatus === 'lost'
                    ? <span className="flex items-center gap-1 text-[11px] text-red-400"><WifiOff size={11} /> Lost</span>
                    : v.gpsStatus === 'weak'
                    ? <span className="flex items-center gap-1 text-[11px] text-amber-400"><Wifi size={11} /> Weak</span>
                    : <span className="flex items-center gap-1 text-[11px] text-emerald-400"><Wifi size={11} /> Strong</span>
                  }
                </td>
                <td className="px-4 py-3">
                  {v.driver
                    ? <p className="text-xs text-zinc-300">{v.driver}</p>
                    : <p className="text-xs text-zinc-600 italic">Unassigned</p>
                  }
                  {v.jobId && <p className="text-[10px] text-amber-400 font-mono">{v.jobId}</p>}
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs text-zinc-400 flex items-start gap-1 max-w-[140px]">
                    <MapPin size={9} className="mt-0.5 flex-shrink-0" />
                    <span className="truncate">{v.lastLocation}</span>
                  </p>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-300 tabular-nums">{v.odometer.toLocaleString()} mi</td>
                <td className="px-4 py-3">
                  <div>
                    <div className="flex items-center justify-between text-[10px] mb-1">
                      <span className={v.fuelLevel < 25 ? 'text-red-400' : v.fuelLevel < 40 ? 'text-amber-400' : 'text-zinc-400'}>{v.fuelLevel}%</span>
                    </div>
                    <div className="h-1 w-16 bg-zinc-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${v.fuelLevel}%`,
                          backgroundColor: v.fuelLevel < 25 ? '#F87171' : v.fuelLevel < 40 ? '#F59E0B' : '#34D399',
                        }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-medium font-mono ${v.nextService === 'OVERDUE' ? 'text-red-400' : v.nextService === 'In Service' ? 'text-amber-400' : 'text-zinc-400'}`}>
                    {v.nextService}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => toast.info(`Editing ${v.unit}`)} className="p-1.5 rounded-md text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-all" title="Edit vehicle">
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => toast.error(`${v.unit} removed from fleet`)} className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Remove vehicle from fleet">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}